// Newsletter-Popup (sections/oo-newsletter-popup.liquid).
// Öffnet frühestens nach data-delay Sekunden oder ab data-scroll Prozent Scrolltiefe (diese erst nach 10 Sekunden),
// nie über einem anderen Dialog, dem Cookie-Banner oder während jemand tippt, und nicht mehr, sobald etwas in den
// Warenkorb gelegt wurde (Ereignis oo:cart:added). Geschlossen bleibt es data-dismiss-days
// Tage weg, nach einer Anmeldung dauerhaft. Langfristig gemerkt wird nur mit Einwilligung in Präferenz-Cookies,
// sonst im Sitzungsspeicher bis zum Ende des Besuchs.

export const KEY = 'oo:newsletter:v1';
export const SOURCE_KEY = 'oo:newsletter:source';
export const SUBSCRIBED_DAYS = 365;
export const MIN_SCROLL_WAIT = 10_000;
export const RETRY_WAIT = 5_000;
const DAY = 86_400_000;

function safeStore(name) {
  try {
    return globalThis[name] ?? null;
  } catch {
    // Zugriff kann im privaten Modus oder bei blockierten Cookies werfen.
    return null;
  }
}

// Gemerkter Zustand ('dismissed' oder 'subscribed'), abgelaufene oder kaputte Einträge werden gelöscht.
export function readState(store, now = Date.now()) {
  try {
    const raw = store?.getItem(KEY);
    if (!raw) return null;
    const record = JSON.parse(raw);
    const valid = (record?.state === 'dismissed' || record?.state === 'subscribed') && Number.isFinite(record.until);
    if (valid && record.until > now) return record.state;
    store.removeItem(KEY);
  } catch {
    try {
      store?.removeItem(KEY);
    } catch {
      // Speicher gesperrt: nichts zu tun.
    }
  }
  return null;
}

export function writeState(store, state, days, now = Date.now()) {
  try {
    store?.setItem(KEY, JSON.stringify({ state, until: now + days * DAY }));
    return Boolean(store);
  } catch {
    return false;
  }
}

// Liest den Wert einmal und löscht ihn, damit ein Neuladen das Popup nicht erneut öffnet.
export function takeOnce(store, key) {
  try {
    const value = store?.getItem(key) ?? null;
    if (value !== null) store.removeItem(key);
    return value;
  } catch {
    return null;
  }
}

export function scrollDepth(scrollY, viewportHeight, documentHeight) {
  const scrollable = documentHeight - viewportHeight;
  if (scrollable <= 0) return 0;
  return Math.min(100, Math.max(0, (scrollY / scrollable) * 100));
}

// Das Maskottchen steht auf loading="lazy", damit es den Seitenaufbau nicht bremst. Im geschlossenen Dialog lädt ein
// lazy Bild aber erst beim Öffnen und erscheint dann sichtbar später, deshalb wird es vorher auf eager umgestellt.
export function loadEagerly(img) {
  if (img && img.loading !== 'eager') img.loading = 'eager';
}

export function preferencesAllowed(privacy = globalThis.Shopify?.customerPrivacy) {
  try {
    return privacy?.preferencesProcessingAllowed?.() === true;
  } catch {
    return false;
  }
}

// Shopifys Einwilligungs-API ist nicht auf jeder Seite geladen; ohne Antwort nach 3 Sekunden gilt „nicht erlaubt“.
function loadPrivacyApi(shopify = globalThis.Shopify) {
  if (shopify?.customerPrivacy || typeof shopify?.loadFeatures !== 'function') return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, 3000);
    try {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {
        clearTimeout(timer);
        resolve();
      });
    } catch {
      clearTimeout(timer);
      resolve();
    }
  });
}

function isVisible(element) {
  return Boolean(element && element.getClientRects().length > 0);
}

function isBlocked() {
  if (document.querySelector('dialog[open]')) return true;
  if (isVisible(document.getElementById('shopify-pc__banner'))) return true;
  const active = document.activeElement;
  return Boolean(active?.matches?.('input, textarea, select, [contenteditable="true"]'));
}

class OoNewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.art = this.dialog.querySelector('.oo-nl-popup__art img');
    this.local = safeStore('localStorage');
    this.session = safeStore('sessionStorage');

    if (this.hasAttribute('data-design-mode')) {
      this.initEditor();
      return;
    }

    this.form = document.getElementById(this.dataset.formId);
    this.onSubmit = () => {
      try {
        this.session?.setItem(SOURCE_KEY, 'popup');
      } catch {
        // Ohne Sitzungsspeicher bleibt das Ergebnis nur im Formular sichtbar.
      }
    };
    this.form?.addEventListener('submit', this.onSubmit);
    this.dialog.addEventListener('close', () => this.onClose());

    // Nach dem Absenden lädt Shopify die Seite neu; Erfolg und Fehler stehen dann im Formular.
    const fromPopup = takeOnce(this.session, SOURCE_KEY) === 'popup';
    const succeeded = Boolean(document.querySelector('[data-oo-newsletter-success]'));
    if (succeeded) this.remember('subscribed', SUBSCRIBED_DAYS);
    if (fromPopup && this.querySelector('[data-oo-newsletter-success], [data-oo-newsletter-error]')) {
      this.open();
      return;
    }
    if (succeeded || readState(this.local) || readState(this.session)) return;
    // Wer etwas in den Warenkorb legt, kauft gerade ein: dann für diesen Seitenaufruf nicht mehr stören.
    // Neue Seiten mit gefülltem Warenkorb rendern das Popup gar nicht erst (oo-newsletter-popup.liquid).
    this.onCartAdded = () => this.disarm();
    document.addEventListener('oo:cart:added', this.onCartAdded);
    this.arm();
  }

  disconnectedCallback() {
    if (this.onCartAdded) document.removeEventListener('oo:cart:added', this.onCartAdded);
    this.disarm();
    this.form?.removeEventListener('submit', this.onSubmit);
  }

  arm() {
    const delay = Math.max(10, Number(this.dataset.delay) || 35) * 1000;
    const scroll = Number(this.dataset.scroll) || 0;
    const start = Date.now();
    this.timer = setTimeout(() => this.tryOpen(), delay);
    // Nach dem Seitenaufbau im Hintergrund laden, damit das Bild beim Öffnen schon da ist.
    if (document.readyState === 'complete') loadEagerly(this.art);
    else window.addEventListener('load', () => loadEagerly(this.art), { once: true });
    if (scroll > 0) {
      this.onScroll = () => {
        if (Date.now() - start < MIN_SCROLL_WAIT) return;
        const depth = scrollDepth(window.scrollY, window.innerHeight, document.documentElement.scrollHeight);
        if (depth >= scroll) this.tryOpen();
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
  }

  disarm() {
    clearTimeout(this.timer);
    clearTimeout(this.retry);
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    this.onScroll = null;
  }

  tryOpen() {
    if (this.dialog.open || this.retry) return;
    if (isBlocked()) {
      this.retry = setTimeout(() => {
        this.retry = null;
        this.tryOpen();
      }, RETRY_WAIT);
      return;
    }
    this.disarm();
    this.open();
  }

  open() {
    if (this.dialog.open) return;
    loadEagerly(this.art);
    this.dialog.showModal();
    this.dialog.dispatchEvent(new CustomEvent('oo:dialog:open', { bubbles: true }));
  }

  onClose() {
    if (readState(this.local) === 'subscribed' || readState(this.session) === 'subscribed') return;
    this.remember('dismissed', Number(this.dataset.dismissDays) || 30);
  }

  // Erst für diesen Besuch merken, mit Einwilligung in Präferenz-Cookies zusätzlich für die eingestellten Tage.
  async remember(state, days) {
    writeState(this.session, state, days);
    await loadPrivacyApi();
    if (preferencesAllowed()) writeState(this.local, state, days);
  }

  // Theme-Editor: Abschnitt auswählen öffnet das Popup, abwählen schließt es. Nichts wird gemerkt.
  initEditor() {
    const mine = (event) => event.target instanceof Element && event.target.contains(this);
    loadEagerly(this.art);
    document.addEventListener('shopify:section:select', (event) => mine(event) && !this.dialog.open && this.dialog.showModal());
    document.addEventListener('shopify:section:deselect', (event) => mine(event) && this.dialog.close());
  }
}

if (!customElements.get('oo-newsletter-popup')) customElements.define('oo-newsletter-popup', OoNewsletterPopup);
